import { Box, ButtonBase, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { ChevronDown, LogOut, Moon, Sun, Wrench } from 'lucide-react';
import { useId, useState, type ReactElement } from 'react';
import { useGetIdentity, useLogout, useTheme } from 'react-admin';
import { Link } from 'react-router';

import { ROLE_LABELS, type AdminRole } from '@/admin/adminApi';
import type { BackofficeIdentity } from '@/auth/authProvider';

import { Avatar, Icon, Skeleton } from '../kit';
import { DIAGNOSTIC_PATH } from '../navigation';

/**
 * Commandes de droite de la barre superieure : bascule de theme et menu de
 * l'operateur connecte.
 *
 * Le menu nomme le **role reel**, lu sur `/admin/me` par l'`authProvider` : un
 * exploitant `support` doit savoir avant de cliquer qu'il est en lecture seule.
 * Il porte aussi le diagnostic technique, qui a quitte la barre laterale : c'est
 * un outil de developpeur, pas un objet d'administration.
 */

/**
 * Libelle du role, une fois l'identite lue : `undefined` = `/admin/me` en panne,
 * `null` = aucun role (cas courant d'un compte parent : 200 avec `adminRole:
 * null`, ou 403). Pendant la lecture, l'appelant n'affiche **rien** : dire
 * « indisponible » avant la reponse annoncerait une panne qui n'existe pas.
 */
export const roleLabel = (role: AdminRole | null | undefined): string =>
  role === undefined ? 'Rôle indisponible' : role === null ? 'Sans rôle d’administration' : ROLE_LABELS[role];

/** Cible de 40 px, bordure de la maquette (« Bouton moon »). */
const iconButtonSx = {
  width: 40,
  height: 40,
  border: 1,
  borderColor: 'tokens.border',
  bgcolor: 'tokens.surface',
  color: 'tokens.textMuted',
  '&:hover': { bgcolor: 'tokens.surfaceMuted' },
} as const;

export const ThemeToggle = (): ReactElement => {
  const [theme, setTheme] = useTheme();
  const dark = theme === 'dark';
  const label = dark ? 'Passer en thème clair' : 'Passer en thème sombre';

  return (
    <Tooltip title={label}>
      <IconButton aria-label={label} onClick={() => { setTheme(dark ? 'light' : 'dark'); }} sx={iconButtonSx}>
        <Icon icon={dark ? Sun : Moon} size={18} />
      </IconButton>
    </Tooltip>
  );
};

export const UserMenu = ({ compact }: { readonly compact: boolean }): ReactElement => {
  const { identity, isPending } = useGetIdentity();
  const logout = useLogout();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();

  const current = identity as BackofficeIdentity | undefined;
  const name = current?.fullName ?? '';
  const role = isPending ? null : roleLabel(current?.adminRole);
  const close = (): void => {
    setAnchor(null);
  };

  return (
    <>
      <ButtonBase
        aria-label={`Menu de ${name === '' ? 'l’utilisateur' : name}${role === null ? '' : `, ${role}`}`}
        aria-haspopup="menu"
        aria-expanded={anchor === null ? 'false' : 'true'}
        aria-controls={anchor === null ? undefined : menuId}
        onClick={(event) => {
          setAnchor(event.currentTarget);
        }}
        sx={{ minHeight: 40, minWidth: 40, gap: 1.25, px: compact ? 0 : 1, borderRadius: '10px', textAlign: 'left', '&:hover': { bgcolor: 'tokens.surfaceMuted' } }}
      >
        <Avatar name={name === '' ? '?' : name} tone="blue" />
        {compact ? null : (
          <>
            <Box sx={{ minWidth: 0, maxWidth: 200 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, lineHeight: '18px' }}>
                {name === '' ? '—' : name}
              </Typography>
              {role === null ? (
                // Etat de lecture neutre : une barre, aucun mot.
                <Skeleton width={88} height={10} />
              ) : (
                <Typography noWrap sx={{ fontSize: 11, lineHeight: '14px', color: 'tokens.textMuted' }}>
                  {role}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'inline-flex', color: 'tokens.textMuted' }}>
              <Icon icon={ChevronDown} size={16} />
            </Box>
          </>
        )}
      </ButtonBase>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={anchor !== null}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Sans `tabIndex`, MenuList saute ce bloc au clavier : il se lit, il ne s'active pas. */}
        <Box sx={{ px: 2, py: 1, maxWidth: 280 }}>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 700 }}>
            {name === '' ? '—' : name}
          </Typography>
          {current !== undefined && current.email !== current.fullName ? (
            <Typography noWrap sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
              {current.email}
            </Typography>
          ) : null}
          {role === null ? null : <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>{role}</Typography>}
        </Box>
        <Divider />
        <MenuItem component={Link} to={DIAGNOSTIC_PATH} onClick={close} sx={{ minHeight: 40 }}>
          <ListItemIcon sx={{ color: 'inherit' }}>
            <Icon icon={Wrench} size={18} />
          </ListItemIcon>
          Diagnostic technique
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            void logout();
          }}
          sx={{ minHeight: 40 }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            <Icon icon={LogOut} size={18} />
          </ListItemIcon>
          Se déconnecter
        </MenuItem>
      </Menu>
    </>
  );
};
